import type { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { Form, Formik } from 'formik'
import { Divider, Stack } from '@mui/material'
import type { TFunction } from 'i18next'
import * as Yup from 'yup'
import { Caption1, Body1, H6, Box } from '@dhl-ui/components'

import type * as PreferenceApiTypes from '@dhl/receiver-preference-api'
import { RadioGroupInput } from '@dhl/inputs'
import { DeliveryOptionRadioInput } from './DeliveryOptionRadioInput.js'
import { DisplayAgreedPlaceValue } from './DisplayAgreedPlaceValue.js'
import { DisplayNeighbourValue } from './DisplayNeighbourValue.js'

type FormValues = {
  preference: 'agreedPlace' | 'neighbours'
  agreedPlace: PreferenceApiTypes.AgreedPlaceInterventionPreference | null
}

const createValidationSchema = (t: TFunction) =>
  Yup.object().shape({
    agreedPlace: Yup.object().when('preference', {
      is: 'agreedPlace',
      then: (schema) => schema.nullable().required(t('portal.preferences.agreedPlace.inputIsRequiredErrorMessage')),
      otherwise: (schema) => schema.nullable(),
    }),
  })

type Props = {
  agreedPlace: PreferenceApiTypes.AgreedPlaceInterventionPreference | null
  formId: string
  onConfigureAgreedPlaceClick: () => void
  isAgreedPlacePreferenceAvailable: boolean
  onChange: (isAgreedPlaceEnabled: boolean) => void
}

export const NotHomeForm: FC<Props> = ({
  agreedPlace,
  onConfigureAgreedPlaceClick,
  isAgreedPlacePreferenceAvailable,
  onChange,
  formId,
}) => {
  const { t } = useTranslation()

  const initialValues: FormValues = {
    agreedPlace,
    preference: agreedPlace?.ringDoorbell ? 'agreedPlace' : 'neighbours',
  }

  const handleSubmit = (values: FormValues) => onChange(values.preference === 'agreedPlace')

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={createValidationSchema(t)}
      onSubmit={handleSubmit}
    >
      {({ setFieldValue, values: { agreedPlace: agreedPlaceValue, preference } }) => (
        <Form noValidate id={formId}>
          <Stack gap={2} paddingBottom={2}>
            <Stack gap={1}>
              <H6>{t('portal.preferences.notHome.intro.title')}</H6>
            </Stack>
            <Box p={0.5}>
              <RadioGroupInput name="preference">
                <Stack gap={2}>
                  <DeliveryOptionRadioInput
                    dataDisplayComponent={<DisplayNeighbourValue sx={{ mt: 1 }} />}
                    dataTest="check-neighbours-ringdoorbell"
                    title={t('portal.preferences.neighbours.titleText')}
                    value="neighbours"
                    onChange={() => setFieldValue('preference', 'neighbours')}
                    isSelected={preference === 'neighbours'}
                    hasPreferenceData={preference === 'neighbours'}
                  />
                  {isAgreedPlacePreferenceAvailable && (
                    <DeliveryOptionRadioInput
                      dataDisplayComponent={
                        <DisplayAgreedPlaceValue data={agreedPlaceValue} hasRingdoorbell sx={{ mt: 1 }} />
                      }
                      dataTest="check-agreedPlace-ringdoorbell"
                      title={t('portal.preferences.agreedPlace.titleTextRingDoorbell')}
                      configureButtonText={t('portal.preferences.agreedPlace.configureButtonText')}
                      editButtonText={t('portal.preferences.agreedPlace.editButtonText')}
                      description={
                        preference === 'agreedPlace' && !agreedPlaceValue?.description
                          ? t('portal.preferences.agreedPlace.afterNotHome.description')
                          : undefined
                      }
                      value="agreedPlace"
                      onConfigureOptionClick={onConfigureAgreedPlaceClick}
                      onChange={() => setFieldValue('preference', 'agreedPlace')}
                      isSelected={preference === 'agreedPlace'}
                      hasPreferenceData={!!agreedPlaceValue?.description && preference === 'agreedPlace'}
                    />
                  )}
                </Stack>
              </RadioGroupInput>
            </Box>
            <Divider sx={{ my: 1 }} />
            <Stack gap={1}>
              <H6>{t('portal.preferences.servicePoint.afterNotHome.title')}</H6>
              <Body1>{t('portal.preferences.servicePoint.afterNotHome.description')}</Body1>
              <Caption1>{t('portal.preferences.servicePoint.afterNotHome.disclaimer.0')}</Caption1>
              <Caption1>{t('portal.preferences.servicePoint.afterNotHome.disclaimer.1')}</Caption1>
            </Stack>
          </Stack>
        </Form>
      )}
    </Formik>
  )
}
